import React, { FC } from 'react'
import {
  Box,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import RefreshIcon from '@mui/icons-material/Refresh'
import { SoapCheckBoxes } from '../SoapCheckBoxes'
import { SoapActionBar } from './SoapActionBar'
import { YakurekiDetail, isFailureStatus } from '@/shared/types/yakurekiCore'
import { SoapSectionKeys } from '@/shared/types/yakurekiSoap'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { SPACING } from '@/shared/styles/spacing'
import { useHasAnyContentSelected } from '@/features/yakureki'

// エラー表示のスタイル定数
const ERROR_ALERT_STYLES = {
  container: {
    backgroundColor: colorTokens.special.highlight,
    border: `1px solid ${colorTokens.blue[50]}`,
    borderRadius: 2,
    py: 1.5,
    px: 2,
    mb: 2,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
  },
  iconWrapper: {
    mt: 0.2,
    color: colorTokens.blue[600],
    display: 'flex',
    alignItems: 'center',
  },
  messageText: {
    color: colorTokens.text.primary,
    lineHeight: 1.6,
  },
  subText: {
    color: colorTokens.text.primary,
    mt: 0.5,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  regenerateButton: {
    mx: 0.5,
    p: 0.5,
    color: colorTokens.blue[600],
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.15)',
    },
  },
}

// 情報アイコンコンポーネント
const InfoIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
      fill="currentColor"
    />
  </svg>
)

interface SoapContentAreaProps {
  patientDetail: YakurekiDetail | null
  isEditMode: boolean
  medications: string[]
  onCopyClick: () => void
  onEditModeToggle: () => void
  onBulkReplaceClick: () => void
  onRegenerateClick: () => void
  onAIChatClick?: () => void
  onHistoryClick?: () => void
  copySectionToClipboard: (key: SoapSectionKeys) => void
  reloadPatientDetail: () => Promise<void>
}

/**
 * SOAP表示コンテンツエリアコンポーネント
 *
 * @description
 * ADR-055 対応: TanStack Query から選択状態を取得するよう移行。
 * SelectionContext への依存を削除。
 */
export const SoapContentArea: FC<SoapContentAreaProps> = ({
  patientDetail,
  isEditMode,
  medications,
  onCopyClick,
  onEditModeToggle,
  onBulkReplaceClick,
  onRegenerateClick,
  onAIChatClick,
  onHistoryClick,
  copySectionToClipboard,
  reloadPatientDetail,
}) => {
  const theme = useTheme()

  // ADR-055: TanStack Query から選択状態を取得
  const hasAnyContentSelected = useHasAnyContentSelected(patientDetail?.id)

  // SOAPセクションの定義
  const soapSections: Array<{
    key: SoapSectionKeys
    label: string
  }> = [
    { key: 'subjectives', label: 'S（主観的情報）' },
    { key: 'objectives', label: 'O（客観的情報）' },
    { key: 'assessments', label: 'A（評価）' },
    { key: 'educational_plans', label: 'EP（教育計画）' },
    { key: 'care_plans', label: 'CP（ケア計画）' },
    { key: 'observational_plans', label: 'OP（観察計画）' },
  ]

  if (!patientDetail) {
    return (
      <Box
        sx={{
          width: '50%',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
          [theme.breakpoints.down('md')]: {
            width: '100%',
            minHeight: '50vh',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
          },
          backgroundColor: colorTokens.grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">読み込み中...</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '50%',
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
        [theme.breakpoints.down('md')]: {
          width: '100%',
          minHeight: '50vh',
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
        },
        backgroundColor: colorTokens.grey[100],
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Main content area */}
      <Box
        sx={{
          flex: '1 1 auto',
          pl: SPACING.card,
          pr: 1.5,
          py: SPACING.mainContent,
          [theme.breakpoints.down('sm')]: {
            pl: SPACING.mainContent,
            pr: SPACING.mainContent,
            py: SPACING.mainContent,
          },
        }}
      >
        <Stack
          p={SPACING.card}
          spacing={SPACING.sectionGap}
          bgcolor={colorTokens.background.paper}
          borderRadius={2}
          direction="column"
          alignItems="flex-start"
          sx={[
            {
              transition: 'all 0.3s ease',
            },
            isEditMode
              ? {
                  border: `2px solid ${colorTokens.blue[500]}`,
                }
              : {
                  border: `1px solid ${colorTokens.border.light}`,
                },
            isEditMode
              ? {
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)',
                }
              : {
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                },
          ]}
        >
          {/* アクションバー */}
          <SoapActionBar
            yakurekiDetail={patientDetail}
            isEditMode={isEditMode}
            onCopyClick={onCopyClick}
            onEditModeToggle={onEditModeToggle}
            onBulkReplaceClick={onBulkReplaceClick}
            onAIChatClick={onAIChatClick}
            onHistoryClick={onHistoryClick}
          />

          {/* 文字起こしエラー表示 - デザインシステムの情報系アラートスタイルを適用 */}
          {isFailureStatus(patientDetail.status) && (
            <Box sx={ERROR_ALERT_STYLES.container}>
              {/* 情報アイコン */}
              <Box sx={ERROR_ALERT_STYLES.iconWrapper}>
                <InfoIcon />
              </Box>
              {/* メッセージ */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={ERROR_ALERT_STYLES.messageText}>
                  {patientDetail.status === 'failed_short'
                    ? '録音時間が短すぎるため、文字起こしができませんでした。'
                    : '服薬指導会話が正確に認識できませんでした。'}
                </Typography>
                {/* 再生成ボタンを含む補助テキスト */}
                {patientDetail.status === 'failed' && (
                  <Typography sx={ERROR_ALERT_STYLES.subText}>
                    お手数ですが、
                    <Tooltip title="SOAP再生成" placement="top">
                      <IconButton
                        onClick={onRegenerateClick}
                        sx={ERROR_ALERT_STYLES.regenerateButton}
                      >
                        <RefreshIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    再生成をお試しください。
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {/* SOAP セクション */}
          {patientDetail.soaps &&
            soapSections.map((section) => (
              <SoapCheckBoxes
                key={`${section.key}-${patientDetail.id}`}
                soapKey={section.key}
                copySection={copySectionToClipboard}
                yakurekiDetail={patientDetail}
                loadYakurekiDetail={reloadPatientDetail}
                isEditMode={isEditMode}
                medications={medications}
              />
            ))}
          {/* 下部ボタンエリア */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mt: 3,
            }}
          >
            {/* 左端: コピーボタン */}
            <Tooltip
              title={
                hasAnyContentSelected
                  ? 'クリップボードにコピー'
                  : 'コピーする内容がありません'
              }
              placement="top"
              arrow
            >
              <Box component="span" sx={{ display: 'inline-block' }}>
                <Box
                  component="button"
                  onClick={() => {
                    if (!hasAnyContentSelected) return
                    onCopyClick()
                  }}
                  disabled={!hasAnyContentSelected}
                  sx={[
                    {
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      border: 'none',
                      minHeight: 40,
                      transition: 'all 0.2s ease',
                    },
                    hasAnyContentSelected
                      ? {
                          backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        }
                      : {
                          backgroundColor: 'rgba(158, 158, 158, 0.12)',
                        },
                    hasAnyContentSelected
                      ? {
                          cursor: 'pointer',
                        }
                      : {
                          cursor: 'not-allowed',
                        },
                    hasAnyContentSelected
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0.6,
                        },
                    hasAnyContentSelected
                      ? {
                          '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.15)',
                          },
                        }
                      : {
                          '&:hover': {
                            backgroundColor: 'rgba(158, 158, 158, 0.18)',
                          },
                        },
                    hasAnyContentSelected
                      ? {
                          '&:active': {
                            backgroundColor: 'rgba(33, 150, 243, 0.2)',
                          },
                        }
                      : {
                          '&:active': {
                            backgroundColor: 'rgba(158, 158, 158, 0.22)',
                          },
                        },
                  ]}
                >
                  <ContentCopyIcon
                    fontSize="small"
                    sx={[
                      hasAnyContentSelected
                        ? {
                            color: colorTokens.blue[500],
                          }
                        : {
                            color: 'rgba(0, 0, 0, 0.26)',
                          },
                    ]}
                  />
                  <Typography
                    sx={[
                      {
                        ml: 0.8,
                      },
                      hasAnyContentSelected
                        ? {
                            color: colorTokens.blue[500],
                          }
                        : {
                            color: colorTokens.grey[500],
                          },
                    ]}
                  >
                    コピー
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            {/* AI判例説明 */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <SmartToyIcon
                sx={{
                  fontSize: 16,
                  color: colorTokens.grey[500],
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                : AIが追加で挿入した箇所
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
